/**
 * Generates HTML email template for organization approval confirmation.
 * Sent when an organization's account has been approved.
 * Guides the user through platform configuration and banking/KYC setup.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';

/**
 * Build the organization approval confirmation email HTML
 *
 * @param {Object} params
 * @param {string} params.firstName - Approved user's first name
 * @param {string} params.configurationUrl - URL to finalize account configuration
 * @param {string} params.bankingUrl - URL to activate receiving account
 * @returns {string} HTML email body
 */
export const buildOrgApprovalEmailHtml = ({
  firstName,
  configurationUrl,
  bankingUrl,
  organization,
}) => {
  return `${emailDocOpen({ title: 'Organization Approval Confirmed' })}

${emailHeader({ title: 'Organization Approval Confirmed', subtitle: '', organization, isBroker: true })}

${spacer()}

<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
  <tr>
    <td style="padding:16px 32px;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td>
            <span style="font-size:15px;font-weight:600;color:#111827;">Welcome to TrustJet: Platform Activation & Underwriting Setup</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  </table>

${spacer(16)}

<!-- Greeting -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:28px 32px 28px;">
      <p style="margin:0 0 16px;font-size:15px;color:#111827;font-weight:600;">Hi ${firstName},</p>
      <p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.7;">
        Welcome to TrustJet! We are ready to integrate your brokerage so you can begin utilizing our risk intelligence and centralized payment infrastructure.
      </p>
      <p style="margin:0 0 14px;font-size:14px;color:#374151;line-height:1.7;">
        Getting your account fully operational involves two steps: configuring your TrustJet profile and completing the commercial underwriting protocol for your dedicated receiving account.
      </p>
      <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
        Please complete both steps below to initiate your setup.
      </p>
    </td>
  </tr>
</table>

${spacer(16)}

<!-- Step 1 -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:28px 32px 0;">
      <p style="margin:0;font-size:16px;font-weight:700;color:#111827;">Step 1: TrustJet Platform Configuration</p>
    </td>
  </tr>
  <tr>
    <td style="padding:14px 32px 0;">
      <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
        First, customize your environment to your brand and set up your core profile
      </p>
    </td>
  </tr>
  <tr>
    <td style="padding:20px 32px 0;">
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td style="background-color:#22315e;border-radius:24px;">
            <a href="${configurationUrl}" target="_blank" style="display:inline-block;padding:12px 28px;font-size:14px;color:#FFFFFF;text-decoration:none;font-weight:500;font-family:'Rethink Sans',sans-serif;">
              Finalize Account Configuration
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:20px 32px 28px;">
      <p style="margin:0 0 6px;font-size:13px;font-weight:600;color:#374151;">You will need:</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;Your brand logo (PNG or SVG preferred)
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;Your default Client Contract template
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>

${spacer(16)}

<!-- Step 2 -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:28px 32px 0;">
      <p style="margin:0;font-size:16px;font-weight:700;color:#111827;">Step 2: Banking KYC &amp; Underwriting</p>
    </td>
  </tr>
  <tr>
    <td style="padding:14px 32px 0;">
      <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
        Next, to activate your receiving accounts, allowing clients to pay directly into your TrustJet ecosystem, federal banking regulations require a standard KYC protocol.
      </p>
    </td>
  </tr>
  <tr>
    <td style="padding:16px 32px 0;">
      <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
        <strong>Security Note:</strong> To ensure PCI compliance and the absolute security of your financial data, your sensitive underwriting documents are routed directly to our banking partner, <em>Priority Commerce</em>, for processing and secure, encrypted storage. TrustJet does not see or store these financial documents.
      </p>
    </td>
  </tr>
  <tr>
    <td style="padding:20px 32px 0;">
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td style="background-color:#22315e;border-radius:24px;">
            <a href="${bankingUrl}" target="_blank" style="display:inline-block;padding:12px 28px;font-size:14px;color:#FFFFFF;text-decoration:none;font-weight:500;font-family:'Rethink Sans',sans-serif;">
              Activate Receiving Account
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:20px 32px 0;">
      <p style="margin:0 0 6px;font-size:13px;font-weight:600;color:#374151;">You will need:</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;A valid Driver's License
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;3 months of your most recent bank statements
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;Your Brokerage's Incorporation documents
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:20px 32px 0;">
      <p style="margin:0 0 6px;font-size:13px;font-weight:700;color:#111827;">If our banking partner requires additional screening, you may also be asked to provide some or all of the following:</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;Full Master Processing Agreement (MPA)
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;Voided check for your primary operating account
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;Government-issued Photo ID for all individuals with 25% or greater ownership
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;3 months of your most recent processing statements (if you're currently using another charter invoicing provider, YTD financials are acceptable in place of these statements)
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;ARC/IATA Code
          </td>
        </tr>
        <tr>
          <td style="padding:4px 0 4px 12px;font-size:13px;color:#374151;line-height:1.6;">
            &#8226;&nbsp;&nbsp;Your last 2 years of Business Tax Returns or Audited Financials (for brokerages with over $50k monthly volume only)
          </td>
        </tr>
      </table> 
    </td>
  </tr>
  <tr>
    <td style="padding:20px 32px 28px;">
      <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
        Once that's done, our team will notify you when your underwriting has been accepted so you can create invoices and accept payments. We look forward to a long and healthy partnership!
      </p>
    </td>
  </tr> 

  <tr>
    <td style="padding:10px 32px 28px;">
      <p style="margin:0 0 8px;font-size:14px;color:#374151;line-height:1.7;">Best Regards,</p>
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td style="border-left:3px solid #22315e;padding-left:10px;">
            <p style="margin:0;font-size:14px;color:#111827;font-weight:600;">The TrustJet Team</p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>

${spacer(20)}

${emailFooter({ organization })}

${emailDocClose()}`;
};
